import { useMemo, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightning, Gauge, ChartBar } from "@phosphor-icons/react";
import {
  DashboardBlock,
  DashboardBlockHeader,
  DashboardBlockContent,
} from "../layout/DashboardBlock";
import { BlockLoadingState } from "../states/BlockStates";
import { useEnergyPerformanceData, EPCLabel, EPCData } from "shared/components/maintenance/hooks";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar, getElementAtEvent } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

// EPC Colors matching the energieprestaties page
const EPC_COLORS: Record<string, string> = {
  "A++": "#22C55E",
  "A+": "#22C55E",
  A: "#22C55E",
  B: "#5B7304",
  C: "#F59E0B",
  D: "#F97316",
  E: "#EA8162",
  F: "#EA8162",
  G: "#EA8162",
  Onbekend: "#838C89",
  "Gemeenschappelijke delen": "#31403B",
  Ontbrekend: "#D1D5DB",
};

interface EPCMetricsBlockNewProps {
  isLoading?: boolean;
}

export const EPCMetricsBlockNew = ({
  isLoading = false,
}: EPCMetricsBlockNewProps) => {
  const navigate = useNavigate();
  const chartRef = useRef<any>(null);
  const { epcData, isLoading: loading } = useEnergyPerformanceData();

  // Prepare label distribution data
  const labelDistribution = useMemo(() => {
    if (!epcData || epcData.length === 0) return null;

    const labelCounts: Record<string, number> = {};
    const orderedLabels = [
      EPCLabel.A_PLUS_PLUS,
      EPCLabel.A_PLUS,
      EPCLabel.A,
      EPCLabel.B,
      EPCLabel.C,
      EPCLabel.D,
      EPCLabel.E,
      EPCLabel.F,
      EPCLabel.G,
      EPCLabel.UNKNOWN,
    ];

    // Initialize with 0
    orderedLabels.forEach((label) => {
      labelCounts[label] = 0;
    });

    // Count units only (exclude building-level entries)
    epcData.forEach((item: EPCData) => {
      if (item.unitId && item.currentEPC?.label) {
        const label = item.currentEPC.label;
        if (labelCounts.hasOwnProperty(label)) {
          labelCounts[label]++;
        }
      }
    });

    // Filter to only show labels with values
    const labels = orderedLabels.filter((label) => labelCounts[label] > 0);
    const values = labels.map((label) => labelCounts[label]);
    const colors = labels.map((label) => EPC_COLORS[label] || "#838C89");

    return { labels, values, colors };
  }, [epcData]);

  // Chart options for bar chart
  const barOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#5B7304",
          borderWidth: 1,
          padding: 8,
          titleFont: { size: 11 },
          bodyFont: { size: 10 },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          ticks: {
            font: { size: 9 },
            stepSize: 5,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    }),
    []
  );

  const barChartData = labelDistribution
    ? {
        labels: labelDistribution.labels,
        datasets: [
          {
            data: labelDistribution.values,
            backgroundColor: labelDistribution.colors,
            borderRadius: 4,
            barThickness: 14,
          },
        ],
      }
    : null;

  // Handle chart click to navigate with filter
  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!chartRef.current || !labelDistribution) return;

      const elements = getElementAtEvent(chartRef.current, event);
      if (elements.length > 0) {
        const index = elements[0].index;
        const clickedLabel = labelDistribution.labels[index];

        // Stop event propagation to prevent the Link from navigating
        event.preventDefault();
        event.stopPropagation();

        // Navigate with the EPC label filter
        navigate(`/renovation/energieprestaties?epcLabel=${encodeURIComponent(clickedLabel)}`);
      }
    },
    [labelDistribution, navigate]
  );

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!epcData || epcData.length === 0)
      return { total: 0, withEPC: 0, coverage: 0 };
    const units = epcData.filter((item: EPCData) => item.unitId);
    const total = units.length;
    const withEPC = units.filter(
      (item: EPCData) =>
        item.currentEPC?.label &&
        item.currentEPC.label !== EPCLabel.UNKNOWN
    ).length;
    const coverage = total > 0 ? Math.round((withEPC / total) * 100) : 0;
    return { total, withEPC, coverage };
  }, [epcData]);

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<Lightning weight="duotone" size={20} />}
        title="EPC Prestaties"
        action={
          <Link
            to="/renovation/energieprestaties"
            className="text-moss-primary text-xs hover:underline"
          >
            Bekijk details
          </Link>
        }
      />
      <Link to="/renovation/energieprestaties">
        <DashboardBlockContent interactive padding="md">
          {isLoading || loading ? (
            <BlockLoadingState message="EPC gegevens aan het ophalen..." />
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="flex items-center justify-between">
                <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                  <Gauge weight="duotone" size={14} />
                  <span>EPC Dekking</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span>
                    <span className="font-semibold text-moss-primary">
                      {summaryStats.withEPC}
                    </span>{" "}
                    / {summaryStats.total} units
                  </span>
                  <span className="rounded bg-moss-secondary-light px-1.5 py-0.5 text-xs font-medium text-moss-primary">
                    {summaryStats.coverage}%
                  </span>
                </div>
              </div>

              {/* EPC Label Distribution Bar Chart */}
              {barChartData && (
                <div className="space-y-2">
                  <div className="text-neutral-secondary-mid flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <ChartBar weight="duotone" size={14} />
                      <span>Label Verdeling</span>
                    </div>
                    <span className="text-[10px] italic">Klik op label voor filter</span>
                  </div>
                  <div className="h-[140px] w-full cursor-pointer">
                    <Bar
                      ref={chartRef}
                      data={barChartData}
                      options={barOptions}
                      onClick={handleChartClick}
                    />
                  </div>
                </div>
              )}

              {/* No data message */}
              {(!labelDistribution || labelDistribution.values.length === 0) &&
                !loading && (
                  <div className="text-neutral-secondary-mid py-4 text-center text-xs">
                    Geen EPC gegevens beschikbaar
                  </div>
                )}
            </div>
          )}
        </DashboardBlockContent>
      </Link>
    </DashboardBlock>
  );
};
